
  import {Component, Prop, Vue} from "vue-property-decorator";
  import {Barrier} from "@/modules/barriers/interfaces/barrier.interface";
  import CarNumber from "@/components/CarNumber.vue";
  import BarrierButton from "@/modules/barriers/components/BarrierButton.vue";
  import {bus} from '@/plugins/bus';
  import {IAuth} from "@/modules/Auth/interfaces/auth.interface";
  import GateActions from "@/modules/barriers/components/GateActions.vue";
  import Sortable from 'sortablejs';
  import {ICarStore} from "@/modules/Car/interfaces/car-store.interface";
  import {similarNumbers} from "@/modules/barriers/helpers/similar-numbers";

  enum OpenBarrierErrorCode {
    NoExist,
    NoAccess,
    OtherCorp,
  }

  @Component({
    components: {
      GateActions,
      CarNumber,
      BarrierButton,
    }
  })
  export default class BarriersList extends Vue {
    @Prop() barriersList: Barrier.Entity[];
    @Prop() enterByQR: boolean;
    @Prop() listWasLoaded: boolean;
    @Prop() parkId: number;
    @Prop() userInfo: { email: string; phone: string; };

    $refs: any;
    publicPath = process.env.VUE_APP_PUBLIC_URL;
    carNumberMode = Barrier.CarNumberMode;
    gateType = Barrier.GateType;
    sortable: Sortable | null = null;
    displayChangeOrder = false;

    get userAccess(): IAuth.UserAccess { return this.$store.state.auth.userAccess; }

    mounted() {
      this.initSortable();

      bus.$on(Barrier.BusEvents.ClearListOrdering, () => {
        if (this.sortable) {
          this.sortable.sort(this.barriersList.map(item => item.camId.toString()));
        }
      });
    }

    replaceRub(str) {
      return str ? str.replaceAll('руб.', '₽') : '';
    }

    printStr2(str2) {
      let result = str2 ? str2.replaceAll('\r', '<br>') : '';
      result = this.replaceRub(result);
      return result;
    }

    async openCarPopup(barrier: Barrier.Entity) {
      if (barrier.carId === -1) return; // Не открывать карточку авто, если там нет мащины.

      if (!barrier.carId && !barrier.scOpenCardMakeCopy) {
        this.openErrorPopup(OpenBarrierErrorCode.NoExist, barrier);
        return;
      }

      if (barrier.scCarOtherCorp) {
        this.openErrorPopup(OpenBarrierErrorCode.OtherCorp, barrier);
        return;
      }

      if (!barrier.scOpenCarCard) {
        this.openErrorPopup(OpenBarrierErrorCode.NoAccess, barrier);
        return;
      }

      if (barrier.similarNumberFlag) {
        await similarNumbers.checkNumbers(barrier);
        return;
      }

      if (!this.enterByQR) {
        bus.$emit(Barrier.BusEvents.StopLoadingBarriers);
        let payload: ICarStore.IPopupPayload = {
          carId: barrier.carId,
          cashSum: barrier.tSum,
          cashParkId: barrier.parkId,
          closeAction: async () => {
            bus.$emit(Barrier.BusEvents.RestartLoadBarriers);
          },
        };
        if (barrier.carId === 0 && barrier.scOpenCardMakeCopy) {
          payload.createMode = true;
          payload.carNumber = barrier.carNumber;
          payload.presetFields = [
            { prop: 'carNumber', value: barrier.carNumber, },
          ];
        }
        await this.$store.dispatch('car/openCarPopup', payload);
      }

      if (this.barriersList && this.barriersList.length === 1 && barrier.mode === Barrier.CarNumberMode.NoCar) {
        bus.$emit('numberInputFocus');
      }
    }

    async openErrorPopup(openBarrierErrorCode: OpenBarrierErrorCode, barrier: Barrier.Entity) {
      const ErrorMessages = {
        [OpenBarrierErrorCode.NoExist]: `На машину ${barrier.carNumber} не создана карточка`,
        [OpenBarrierErrorCode.NoAccess]: `У Вас недостаточно прав для просмотра карточки данного автомобиля.`,
        [OpenBarrierErrorCode.OtherCorp]: `<p>Недостаточно прав</p>
          <p>Вы не можете просмотреть карточку этого автомобиля, так как он уже добавлен на текущую парковку в другой корпорации.</p>
          <p>Если вам нужен доступ к автомобилю, обратитесь в управляющую компанию.</p>`,
      };

      await this.$store.dispatch('confirm/open', {
        class: 'barriers-list__open-barrier-error',
        title: 'Внимание',
        text: ErrorMessages[openBarrierErrorCode],
        cancel: { text: '', },
        confirm: { text: 'Ок', },
      }, { root: true, });
    }

    async openBalance(barrier: Barrier.Entity) {
      bus.$emit(Barrier.BusEvents.StopLoadingBarriers);
      await this.$store.dispatch('dialog/openDialog', {
        blockName: 'payment',
        dialogName: 'payByCardPopup',
        payload: {
          carNumber: barrier.carNumber,
          enterByQR: this.enterByQR,
          tSum: barrier.tSum,
          closeBalance: true,
          phone: this.userInfo.phone,
          email: this.userInfo.email,
          closeAction: async () => {
            bus.$emit(Barrier.BusEvents.RestartLoadBarriers);
          },
        }
      });
    }

    async openClaims(barrier: Barrier.Entity) {
      bus.$emit(Barrier.BusEvents.StopLoadingBarriers);
      await this.$store.dispatch('dialog/openDialog', {
        blockName: 'payment',
        dialogName: 'claimsPopup',
        payload: {
          carNumber: barrier.carNumber,
          tSum: barrier.tSum,
          phone: this.userInfo.phone,
          email: this.userInfo.email,
          closeAction: async () => {
            bus.$emit(Barrier.BusEvents.RestartLoadBarriers);
          },
        }
      });
    }

    updateAllCarNumbers() {
      this.barriersList.forEach((barrier: Barrier.Entity) => {
        const refName = `carNumber-${barrier.camId}-${barrier.carId}`;
        if (this.$refs[refName]) this.$refs[refName][0].prepareData();
      });
    }

    openTrailer(barrier: Barrier.Entity) {
      let gateActions = this.$refs[`gate$-${barrier.camId}-actions`];
      if (gateActions && gateActions[0]) {
        gateActions[0].openTrailer(barrier);
      }
    }

    async initSortable() {
      const container = this.$refs.listContainer as HTMLElement;

      const sortableOptions: any = {
        handle: '.barrier__drag-icon',
        ghostClass: 'barrier--draggable',
        animation: 150,
        onEnd: (evt: any) => {
          const newOrder = this.sortable.toArray().map(id =>
            this.barriersList.find(item => item.camId.toString() === id)
          );

          if (newOrder) {
            const currentOrder = newOrder.map(item => item.camId);
            const newStorageOrder = { [this.parkId.toString()]: currentOrder };
            let result: any = {};
            let parsedOrder: any = null;

            let storageOrder = localStorage.getItem(Barrier.LocalStorageKeys.ListOrder);
            if (storageOrder) {
              parsedOrder = JSON.parse(storageOrder);
              result =  Object.assign(result, parsedOrder, newStorageOrder);
            }
            else {
              result =  Object.assign(result, newStorageOrder);
            }
            localStorage.setItem(Barrier.LocalStorageKeys.ListOrder, JSON.stringify(result));
          }
        }
      };
      this.sortable = new Sortable(container, sortableOptions);
      this.setCustomListOrder();
    }

    setCustomListOrder() {
      setTimeout(() => {
        let parsedOrder: any = null;
        let storageOrder = localStorage.getItem(Barrier.LocalStorageKeys.ListOrder);
        if (storageOrder && this.sortable) {
          parsedOrder = JSON.parse(storageOrder);
          if (parsedOrder[this.parkId.toString()]) {
            this.sortable.sort(parsedOrder[this.parkId.toString()].map(item => item.toString()));
          }
        }
      }, 100);
    }

    beforeDestroy() {
      if (this.sortable) {
        this.sortable.destroy();
      }

      bus.$off(Barrier.BusEvents.ClearListOrdering);
    }
  }
